import React from 'react';
import { AlertTriangle, CheckCircle, Clock, Calendar } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { useDutyRosterEmployeeData } from '@/hooks/useDutyRosterEmployeeData';
import { getResidencyColorClasses } from '@/utils/residencyUtils';

interface DutyRosterEmployeeInfoProps {
  employeeId: string | null;
  employeeName?: string;
  otDate?: string;
  className?: string;
  onShiftSelectionNeeded?: (needed: boolean) => void;
  onShiftDataLoaded?: (hasWorkingHours: boolean) => void;
}

export const DutyRosterEmployeeInfo: React.FC<DutyRosterEmployeeInfoProps> = ({
  employeeId,
  otDate,
  className = '',
  onShiftSelectionNeeded,
  onShiftDataLoaded
}) => {
  const { data: dutyRosterData, isLoading, error } = useDutyRosterEmployeeData(employeeId, otDate);

  // Notify parent component when shift selection is needed
  React.useEffect(() => {
    if (onShiftSelectionNeeded && !isLoading && employeeId && otDate && dutyRosterData) {
      if (dutyRosterData.isDutyRoster && dutyRosterData.dateValidation) {
        // For duty roster employees, show shift selection if:
        // 1. It's an off day (no shift constraints)
        // 2. It's a holiday override (no shift constraints)
        // 3. It's a working day but we want to show available shifts
        const needsShiftSelection = dutyRosterData.dateValidation.isOffDay || 
                                  dutyRosterData.dateValidation.isHolidayOverride ||
                                  dutyRosterData.dateValidation.isWorkingDay;
        onShiftSelectionNeeded(needsShiftSelection);
      } else {
        // For non-duty roster employees, use existing logic
        onShiftSelectionNeeded(!dutyRosterData.isDutyRoster);
      }
    }
  }, [dutyRosterData, isLoading, employeeId, otDate, onShiftSelectionNeeded]);

  // Notify parent when data is loaded
  React.useEffect(() => {
    if (onShiftDataLoaded && !isLoading && employeeId) {
      onShiftDataLoaded(true);
    }
  }, [isLoading, onShiftDataLoaded, employeeId]);

  // Don't render if no employee is selected
  if (!employeeId) {
    return null;
  }

  if (isLoading) {
    return (
      <div className={`${className} w-full`}>
        <div className="flex items-center gap-2 py-1">
          <div className="h-4 w-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
          <div className="h-4 w-32 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
        </div>
      </div>
    );
  }

  if (error || dutyRosterData?.error) {
    return (
      <div className={`${className} w-full`}>
        <div className="flex items-center gap-2 py-1">
          <AlertTriangle className="h-4 w-4 text-red-500" />
          <span className="text-xs text-red-600 dark:text-red-400">
            {dutyRosterData?.error || 'Failed to load employee data'}
          </span>
        </div>
      </div>
    );
  }

  if (!dutyRosterData) {
    return null;
  }

  // Non-duty roster employee - show standard message
  if (!dutyRosterData.isDutyRoster) {
    return (
      <div className={`${className} w-full`}>
        <div className="flex items-center gap-2 py-1">
          <AlertTriangle className="h-4 w-4 text-amber-500 dark:text-amber-400" />
          <span className="text-xs text-amber-600 dark:text-amber-400">
            No active shift assignment found for this employee
          </span>
        </div>
      </div>
    );
  }

  // Duty roster employee without group
  if (!dutyRosterData.groupInfo) {
    return (
      <div className={`${className} w-full`}>
        <div className="flex items-center gap-2 py-1">
          <AlertTriangle className="h-4 w-4 text-red-500" />
          <span className="text-xs text-red-600 dark:text-red-400">
            Employee is duty roster but not assigned to any group
          </span>
        </div>
      </div>
    );
  }

  const { groupInfo, dateValidation } = dutyRosterData;

  if (!dateValidation) {
    return (
      <div className={`${className} w-full`}>
        <div className="flex items-center gap-2 py-1">
          <AlertTriangle className="h-4 w-4 text-red-500" />
          <span className="text-xs text-red-600 dark:text-red-400">
            Failed to validate date for duty roster employee
          </span>
        </div>
      </div>
    );
  }

  // Render duty roster employee information
  return (
    <div className={`${className} w-full space-y-2`}>
      {/* Group Information */}
      <div className="flex items-center gap-2 py-1">
        <CheckCircle className="h-4 w-4 text-green-500" />
        <span className="text-xs text-green-600 dark:text-green-400">
          Duty Roster Employee
        </span>
        <Badge 
          variant="outline" 
          className={`text-xs ${getResidencyColorClasses(groupInfo.residencyType)}`}
        >
          {groupInfo.groupName} ({groupInfo.residencyType})
        </Badge>
      </div>

      {/* Date Status Information */}
      <div className="flex items-center gap-2 py-1">
        {dateValidation.isHolidayOverride ? (
          <>
            <Calendar className="h-4 w-4 text-blue-500" />
            <span className="text-xs text-blue-600 dark:text-blue-400">
              Extended holiday - no overtime constraints. You can select any shift for overtime.
            </span>
          </>
        ) : dateValidation.isOffDay ? (
          <>
            <Clock className="h-4 w-4 text-purple-500" />
            <span className="text-xs text-purple-600 dark:text-purple-400">
              Off day - no overtime constraints. You can select any shift for overtime.
            </span>
          </>
        ) : dateValidation.isWorkingDay ? (
          <>
            <CheckCircle className="h-4 w-4 text-green-500" />
            <span className="text-xs text-green-600 dark:text-green-400">
              Working day - select a different shift for overtime.
            </span>
          </>
        ) : (
          <>
            <AlertTriangle className="h-4 w-4 text-amber-500" />
            <span className="text-xs text-amber-600 dark:text-amber-400">
              Date not found in group schedule.
            </span>
          </>
        )}
      </div>

      {/* Current Shift Information (if working day) */}
      {dateValidation.isWorkingDay && dateValidation.currentShift && (
        <div className="flex items-center gap-2 py-1">
          <Clock className="h-4 w-4 text-gray-500" />
          <span className="text-xs text-gray-600 dark:text-gray-400">
            Current assigned shift: {dateValidation.currentShift.name} ({dateValidation.currentShift.code})
          </span>
        </div>
      )}
    </div>
  );
};

export default DutyRosterEmployeeInfo;
